/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, September 11th 2022, 10:03:53 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import { useListEditorContext } from "lib/providers";
import { DeleteOSItemDialog } from "version-control/components/DeleteOSItemDialog";
import { OSActionButton } from "ui-widgets";
import { ObjectValue } from "lib/types";

interface ListEditorListItemProps<T> extends Pick<React.ComponentProps<typeof DeleteOSItemDialog>, "useDeleteOSItem"> {
  listItemId: ObjectValue<T> & (number | string);
  primaryText: React.ReactNode;
  secondaryText: React.ReactNode;
  onClickEditItem: (itemId: ObjectValue<T> & (number | string)) => void;
  onDeleteItemCallback: () => void;
  listItemIcon: React.ReactNode;
}

/**
 * A list editor item that works seemlessly with items ListEditorProvider.
 */

export const APIOne2ManyListEditorItem = <T,>(props: ListEditorListItemProps<T>) => {
  const { handleEditItem, onDeleteItemCallback, updateListAndSelectIfNewItem } = useListEditorListItem(props);

  const { primaryText, secondaryText, listItemIcon, listItemId, useDeleteOSItem } = props;

  return (
    <Paper elevation={8}>
      <ListItem alignItems="flex-start">
        <ListItemAvatar>{listItemIcon}</ListItemAvatar>
        <ListItemText
          primary={primaryText}
          secondary={
            <Stack direction="row" sx={{ justifyContent: "space-between" }}>
              {secondaryText}
              <Stack direction="row" spacing={2}>
                <OSActionButton actionStyle="edit" onClick={handleEditItem}>
                  Edit
                </OSActionButton>
                <DeleteOSItemDialog<T>
                  useDeleteOSItem={useDeleteOSItem}
                  itemId={listItemId}
                  onDelete={onDeleteItemCallback}
                  onRestore={updateListAndSelectIfNewItem}
                >
                  {({ handleOpen }) => (
                    <OSActionButton onClick={handleOpen} actionStyle="delete">
                      Delete
                    </OSActionButton>
                  )}
                </DeleteOSItemDialog>
              </Stack>
            </Stack>
          }
        />
      </ListItem>
    </Paper>
  );
};

const useListEditorListItem = <T,>({
  listItemId,
  onClickEditItem,
  onDeleteItemCallback,
}: ListEditorListItemProps<T>) => {
  const { removeItem, updateListAndSelectIfNewItem } = useListEditorContext<T>();

  return {
    updateListAndSelectIfNewItem,
    handleEditItem: () => onClickEditItem(listItemId),
    onDeleteItemCallback: () => {
      removeItem(listItemId);
      onDeleteItemCallback();
    },
  };
};
